
import { usePaginatedTestResponses } from "@/hooks/usePaginatedTestResponses";
import { MobileResponseCard } from "./responses-table/MobileResponseCard";
import { UnifiedToolbar } from "./responses-table/UnifiedToolbar";
import { SortableTableHead } from "./responses-table/SortableTableHead";
import { ResponsesBulkBar } from "./responses-table/ResponsesBulkBar";
import { FilterType } from "@/types/test-response";
import { DataPagination } from "@/components/ui/data-pagination";
import { useIsMobile } from "@/hooks/use-mobile";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { LoadingState } from "./states/LoadingState";
import { ErrorState } from "./states/ErrorState";
import { ResponseTableRow } from "./responses-table/ResponseTableRow";
import { useRowSelection } from "@/hooks/useRowSelection";

import { useSearchResponses } from "@/hooks/useSearchResponses";
import { useState } from "react";

interface TestResponsesTableProps {
  testId: string;
  canManageRecruitment?: boolean;
  canDelete?: boolean;
}

export const TestResponsesTable = ({ testId, canManageRecruitment = false, canDelete = false }: TestResponsesTableProps) => {
  const isMobile = useIsMobile();
  const [statusColorsEnabled, setStatusColorsEnabled] = useState(false);
  
  const {
    responses,
    totalResponses,
    currentPage,
    totalPages,
    isLoading,
    isError,
    activeFilters,
    setActiveFilters,
    sortOption,
    handleSortChange,
    handlePageChange,
  } = usePaginatedTestResponses(testId);

  
  
  // Add search functionality
  const { 
    searchQuery, 
    handleSearchChange, 
    filteredResponses 
  } = useSearchResponses(responses);
  
  const {
    isSelected,
    toggleRow,
    selectAll,
    clearSelection,
    isAllSelected,
    isIndeterminate,
    getSelectedResponses,
    selectedCount,
  } = useRowSelection();

  const handleFilterChange = (filters: FilterType[]) => {
    setActiveFilters(filters);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      selectAll(filteredResponses);
    } else {
      clearSelection();
    }
  };

  const selectedResponses = getSelectedResponses(filteredResponses);

  // Parse current sort option to extract sort key and direction
  const getSortKeyAndDirection = (sortOption: string) => {
    switch (sortOption) {
      case "recent":
        return { key: "date", direction: "desc" as const };
      case "oldest":
        return { key: "date", direction: "asc" as const };
      case "highestScore":
        return { key: "aiScore", direction: "desc" as const };
      case "lowestScore":
        return { key: "aiScore", direction: "asc" as const };
      case "highestIntegrity":
        return { key: "integrity", direction: "desc" as const };
      case "candidate":
        return { key: "candidate", direction: "asc" as const };
      case "date":
        return { key: "date", direction: "desc" as const };
      case "aiScore":
        return { key: "aiScore", direction: "desc" as const };
      case "integrity":
        return { key: "integrity", direction: "desc" as const };
      default:
        return { key: "date", direction: "desc" as const };
    }
  };

  const { key: currentSortKey, direction: sortDirection } = getSortKeyAndDirection(sortOption);

  // Handle column header sorting
  const handleColumnSort = (sortKey: string) => {
    let newSortOption: string;
    
    if (currentSortKey === sortKey) {
      // Toggle direction for same column
      if (sortDirection === "desc") {
        newSortOption = sortKey === "date" ? "oldest" : 
                       sortKey === "aiScore" ? "lowestScore" : 
                       sortKey;
      } else {
        newSortOption = sortKey === "date" ? "recent" : 
                       sortKey === "aiScore" ? "highestScore" : 
                       sortKey === "integrity" ? "highestIntegrity" :
                       sortKey;
      }
    } else {
      // New column, default to desc for most, asc for candidate
      newSortOption = sortKey === "candidate" ? "candidate" :
                     sortKey === "date" ? "recent" :
                     sortKey === "aiScore" ? "highestScore" :
                     sortKey === "integrity" ? "highestIntegrity" :
                     sortKey;
    }
    
    handleSortChange(newSortOption as any);
  };

  if (isLoading) {
    return <LoadingState />;
  }

  if (isError) {
    return <ErrorState />;
  }

  if (isMobile) {
    return (
      <div className="space-y-4">
        <UnifiedToolbar
          searchQuery={searchQuery}
          onSearch={handleSearchChange}
          sortOption={sortOption}
          onSortChange={handleSortChange}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          statusColorsEnabled={statusColorsEnabled}
          onStatusColorsChange={setStatusColorsEnabled}
        />

        <div className="space-y-3">
          {filteredResponses.map((response) => (
            <MobileResponseCard 
              key={response.id} 
              response={response} 
              canManageRecruitment={canManageRecruitment}
              statusColorsEnabled={statusColorsEnabled}
            />
          ))}
        </div>

        {totalPages > 1 && (
          <DataPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <UnifiedToolbar
        searchQuery={searchQuery}
        onSearch={handleSearchChange}
        sortOption={sortOption}
        onSortChange={handleSortChange}
        activeFilters={activeFilters}
        onFilterChange={handleFilterChange}
        statusColorsEnabled={statusColorsEnabled}
        onStatusColorsChange={setStatusColorsEnabled}
      />

      <ResponsesBulkBar 
        selectedResponses={selectedResponses}
        onClearSelection={clearSelection}
        testId={testId}
      />

      <div className="rounded-md border">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-30">
            <TableRow>
              {canManageRecruitment && (
                <TableHead className="w-12">
                  <Checkbox
                    checked={isIndeterminate(filteredResponses) ? "indeterminate" : isAllSelected(filteredResponses)}
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
              )}
              <SortableTableHead 
                sortKey="candidate"
                currentSortKey={currentSortKey}
                sortDirection={sortDirection}
                onSort={handleColumnSort}
                className="w-[200px]"
              >
                Candidate
              </SortableTableHead>
              <TableHead>Status</TableHead>
              <SortableTableHead 
                sortKey="date"
                currentSortKey={currentSortKey}
                sortDirection={sortDirection}
                onSort={handleColumnSort}
              >
                Date
              </SortableTableHead>
              <SortableTableHead 
                sortKey="aiScore"
                currentSortKey={currentSortKey}
                sortDirection={sortDirection}
                onSort={handleColumnSort}
              >
                AI Score
              </SortableTableHead>
              <SortableTableHead 
                sortKey="integrity"
                currentSortKey={currentSortKey}
                sortDirection={sortDirection}
                onSort={handleColumnSort}
              >
                Integrity
              </SortableTableHead>
              <TableHead className="w-[140px]">Recruitment</TableHead>
              <TableHead className="text-right w-[120px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredResponses.map((response) => (
              <ResponseTableRow 
                key={response.id} 
                response={response} 
                canManageRecruitment={canManageRecruitment}
                canDelete={canDelete}
                isSelected={isSelected(response.id)}
                onSelectionChange={(selected) => {
                  if (selected !== isSelected(response.id)) {
                    toggleRow(response.id);
                  }
                }}
                statusColorsEnabled={statusColorsEnabled}
              />
            ))}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <DataPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};
